import { z } from 'zod';
import { adapterClient, BackendError, handleApiError } from './client';
import { canonicalProjectId, getProjectView } from './projectViews';
import {
  disputeViewSchema,
  disputePresentationSchema,
  disputeEntrySchema,
  disputeTextSchema,
} from '@/types/current-dispute';
import type {
  Dispute,
  DisputeArgument,
  DisputeArgumentType,
  DisputeHistory,
  DisputeMessage,
} from '@/types/dispute';
import { useAuthStore } from '@/stores/authStore';
import {
  completeProjectCommand,
  clearProjectCommand,
  pendingProjectCommand,
} from '@/services/projectCommands';

export async function getPublicDispute(id: string, signal?: AbortSignal) {
  const disputeId = canonicalProjectId(id);
  try {
    const { data } = await adapterClient.get<unknown>(
      '/disputes/' + disputeId,
      { signal }
    );
    const result = disputeViewSchema.safeParse(data);
    if (!result.success || result.data.dispute.disputeId !== disputeId)
      throw new Error(
        'The server returned unsupported dispute data. Retry or report this response.'
      );
    return result.data;
  } catch (error) {
    handleApiError(error, 'read public dispute');
  }
}

const iso = (seconds: number) => new Date(seconds * 1000).toISOString();
export async function apiGetDispute(id: string): Promise<Dispute> {
  const disputeId = canonicalProjectId(id);
  try {
    const { data } = await adapterClient.get(
      '/disputes/' + disputeId + '/presentation'
    );
    const view = disputeViewSchema.parse(data);
    if (view.dispute.disputeId !== disputeId)
      throw new Error('The returned case does not match this dispute.');
    const presentation = disputePresentationSchema.parse(data.presentation);
    const d = view.dispute;
    if (
      presentation.client.role !== 'client' ||
      presentation.coordinator.role !== 'consultant' ||
      new Set([
        presentation.client.accountId,
        presentation.coordinator.accountId,
      ]).size !== 2 ||
      ![
        presentation.client.accountId,
        presentation.coordinator.accountId,
      ].includes(d.openedBy) ||
      ![
        presentation.client.accountId,
        presentation.coordinator.accountId,
      ].includes(d.counterparty)
    )
      throw new Error('The dispute participants do not match this case.');
    const opener =
      d.openedBy === presentation.client.accountId
        ? presentation.client
        : presentation.coordinator;
    return {
      id: d.disputeId,
      projectId: d.projectId,
      milestoneId: d.milestoneId,
      openedByUserId: d.openedBy,
      openedByUserRole:
        d.openedBy === presentation.client.accountId ? 'client' : 'consultant',
      openedBy: {
        id: opener.accountId,
        name: opener.displayName ?? undefined,
        role: opener.role,
      },
      status: 'OPEN',
      openingReason: presentation.openingReason,
      communicationId: d.disputeId,
      createdAt: iso(d.openedAt),
      updatedAt: iso(d.openedAt),
    };
  } catch (error) {
    handleApiError(error, 'read dispute');
  }
}
export async function apiCreateDispute(
  projectId: string,
  milestoneId: string,
  reason: string
): Promise<Dispute> {
  const session = useAuthStore.getState().session;
  if (!session) throw new Error('Log in to open a dispute.');
  const saved = pendingProjectCommand(session.principalId, projectId);
  if (saved && saved.command.kind !== 'dispute-open')
    throw new Error('Finish the pending project request first.');
  if (
    saved?.command.kind === 'dispute-open' &&
    (saved.command.payload.milestoneId !== milestoneId ||
      saved.command.payload.reason !== reason)
  )
    throw new Error(
      'Retry the original dispute opening first; it may already have succeeded.'
    );
  const project = saved ? null : await getProjectView(projectId);
  const milestone = project?.proposals
    .flatMap((p) => p.milestones)
    .find((m) => m.milestoneId === milestoneId);
  const submission = milestone?.submissions[milestone.submissions.length - 1];
  if (!saved && !submission)
    throw new Error(
      'Refresh the current rejected delivery before opening a dispute.'
    );
  const operation = await completeProjectCommand(
    session.principalId,
    saved
      ? null
      : {
          kind: 'dispute-open',
          projectId,
          payload: {
            milestoneId,
            rejectedSubmissionId: submission!.submissionId,
            reason: disputeTextSchema.parse(reason),
          },
        },
    projectId,
    () => {},
    new AbortController().signal
  );
  if (!operation?.receipt?.createdEntityId)
    throw new Error(
      'Opening is still pending. Retry to check the same request.'
    );
  if (useAuthStore.getState().session?.principalId !== session.principalId)
    throw new Error('Session changed. Log in to resume.');
  const dispute = await apiGetDispute(operation.receipt.createdEntityId);
  clearProjectCommand(session.principalId, projectId);
  return dispute;
}
export async function apiGetDisputesByMilestone(
  projectId: string,
  milestoneId: string
): Promise<Dispute[]> {
  const project = await getProjectView(projectId);
  if (!project.activeDisputeId) return [];
  const dispute = await apiGetDispute(project.activeDisputeId);
  return dispute.milestoneId === milestoneId ? [dispute] : [];
}

const pageSchema = z.object({
  items: z.array(disputeEntrySchema),
  nextAfter: z.number().int().positive().safe().nullable(),
  canWrite: z.boolean().optional(),
});
async function entries(id: string, channel: 'arguments' | 'messages') {
  const disputeId = canonicalProjectId(id);
  const result: z.infer<typeof disputeEntrySchema>[] = [];
  let after = 0;
  let canWrite = false;
  do {
    const { data } = await adapterClient.get(
      '/disputes/' + disputeId + '/' + channel,
      { params: { after } }
    );
    const page = pageSchema.parse(data);
    if (channel === 'messages') {
      if (page.canWrite === undefined)
        throw new Error('Missing channel permissions.');
      canWrite = after === 0 ? page.canWrite : canWrite && page.canWrite;
    }
    let previous = after;
    if (
      page.items.some((i) => {
        const invalid =
          i.disputeId !== disputeId ||
          i.cursor <= previous ||
          (i.argumentType === 'MESSAGE') !== (channel === 'messages');
        previous = i.cursor;
        return invalid;
      }) ||
      (page.nextAfter !== null &&
        (page.nextAfter <= after || page.nextAfter !== previous))
    )
      throw new Error('Invalid dispute conversation page.');
    result.push(...page.items);
    if (page.nextAfter === null) return { items: result, canWrite };
    after = page.nextAfter;
  } while (after > 0);
  return { items: result, canWrite };
}
type Entry = z.infer<typeof disputeEntrySchema>;
export function pendingDisputeEntry(
  id: string,
  channel: 'arguments' | 'messages'
) {
  const principal = useAuthStore.getState().session?.principalId;
  const raw = principal
    ? sessionStorage.getItem(
        'abako-dispute-entry:' +
          principal +
          ':' +
          canonicalProjectId(id) +
          ':' +
          channel
      )
    : null;
  if (!raw) return null;
  try {
    return z
      .object({
        content: disputeTextSchema,
        argumentType: disputeEntrySchema.shape.argumentType,
      })
      .parse(JSON.parse(raw));
  } catch {
    return null;
  }
}
const argument = (entry: Entry): DisputeArgument => ({
  id: entry.entryId,
  disputeId: entry.disputeId,
  userId: entry.author.accountId,
  userName: entry.author.displayName ?? undefined,
  userRole: entry.author.role,
  argumentType: entry.argumentType as DisputeArgumentType,
  content: entry.content,
  createdAt: iso(entry.createdAt),
});
const message = (entry: Entry): DisputeMessage => ({
  id: entry.entryId,
  disputeId: entry.disputeId,
  senderId: entry.author.accountId,
  senderName: entry.author.displayName ?? 'Participant',
  senderRole: entry.author.role,
  content: entry.content,
  createdAt: iso(entry.createdAt),
});
export async function apiGetDisputeArguments(
  id: string
): Promise<DisputeArgument[]> {
  try {
    const [dispute, list] = await Promise.all([
      apiGetDispute(id),
      entries(id, 'arguments'),
    ]);
    return [
      {
        id: dispute.id + '-opening',
        disputeId: dispute.id,
        userId: dispute.openedByUserId,
        userName: dispute.openedBy?.name,
        userRole: dispute.openedByUserRole,
        argumentType: 'OPENING',
        content: dispute.openingReason,
        createdAt: dispute.createdAt,
      },
      ...list.items.map(argument),
    ];
  } catch (error) {
    handleApiError(error, 'read dispute arguments');
  }
}
export async function apiGetDisputeMessages(
  id: string
): Promise<{ messages: DisputeMessage[]; canWrite: boolean }> {
  try {
    const page = await entries(id, 'messages');
    return { messages: page.items.map(message), canWrite: page.canWrite };
  } catch (error) {
    handleApiError(error, 'read dispute messages');
  }
}
async function append(
  id: string,
  content: string,
  argumentType: Entry['argumentType']
) {
  const principal = useAuthStore.getState().session?.principalId;
  if (!principal) throw new Error('Log in to publish an entry.');
  const disputeId = canonicalProjectId(id);
  const channel = argumentType === 'MESSAGE' ? 'messages' : 'arguments';
  const key =
    'abako-dispute-entry:' + principal + ':' + disputeId + ':' + channel;
  const payloadSchema = z
    .object({
      entryId: z.string().regex(/^0x[0-9a-f]{32}$/),
      content: disputeTextSchema,
      argumentType: disputeEntrySchema.shape.argumentType,
    })
    .strict();
  const raw = sessionStorage.getItem(key);
  const payload = raw
    ? payloadSchema.parse(JSON.parse(raw))
    : payloadSchema.parse({
        entryId: '0x' + crypto.randomUUID().replace(/-/g, ''),
        content,
        argumentType,
      });
  if (payload.content !== content || payload.argumentType !== argumentType)
    throw new Error(
      'Retry the original text first: its publication may already have succeeded.'
    );
  sessionStorage.setItem(key, JSON.stringify(payload));
  try {
    const { data } = await adapterClient.post(
      '/disputes/' + disputeId + '/' + channel,
      payload
    );
    const saved = disputeEntrySchema.parse(data);
    if (
      saved.disputeId !== disputeId ||
      saved.entryId !== payload.entryId ||
      saved.content !== payload.content ||
      saved.argumentType !== argumentType ||
      saved.author.accountId !== useAuthStore.getState().session?.accountId
    )
      throw new Error(
        'Publication response does not match this entry. Retry the original text.'
      );
    sessionStorage.removeItem(key);
    return saved;
  } catch (error) {
    try {
      handleApiError(error, 'publish dispute entry');
    } catch (mapped) {
      if (
        mapped instanceof BackendError &&
        [400, 401, 403, 404, 413, 422].includes(mapped.statusCode)
      )
        sessionStorage.removeItem(key);
      throw mapped;
    }
  }
}
export async function apiAddDisputeArgument(
  id: string,
  content: string,
  type: DisputeArgumentType
): Promise<DisputeArgument> {
  if (type === 'OPENING')
    throw new Error('The opening argument cannot be edited.');
  return argument(await append(id, content, type));
}
export async function apiSendDisputeMessage(
  id: string,
  content: string
): Promise<DisputeMessage> {
  return message(await append(id, content, 'MESSAGE'));
}
export async function apiGetDisputeHistory(
  id: string
): Promise<DisputeHistory> {
  try {
    const { data } = await adapterClient.get(
      '/disputes/' + canonicalProjectId(id) + '/history'
    );
    const history = z
      .object({
        project: z.object({
          id: z.string(),
          title: z.string(),
          clientId: z.string(),
          clientName: z.string().nullable(),
          consultantId: z.string(),
          consultantName: z.string().nullable(),
        }),
        milestone: z.object({
          id: z.string(),
          title: z.string(),
          state: z.string(),
        }),
        deliverables: z.array(
          z.object({
            version: z.string(),
            submittedAt: z.number().int().nonnegative(),
            documentation: z.string().nullable(),
            links: z.string().nullable(),
            developerName: z.string().nullable(),
          })
        ),
        rejections: z.array(
          z.object({
            version: z.string(),
            rejectedAt: z.number().int().nonnegative(),
            rejectedBy: z.string(),
            rejectedByName: z.string().nullable(),
            reason: z.string(),
          })
        ),
        events: z.array(
          z.object({
            id: z.string(),
            disputeId: z.string(),
            eventType: z.string(),
            eventData: z.record(z.unknown()),
            createdAt: z.number().int().nonnegative(),
          })
        ),
      })
      .parse(data);
    return {
      project: {
        ...history.project,
        clientName: history.project.clientName ?? undefined,
        consultantName: history.project.consultantName ?? undefined,
      },
      milestone: history.milestone,
      deliverables: history.deliverables.map((d) => ({
        ...d,
        submittedAt: iso(d.submittedAt),
        documentation: d.documentation ?? undefined,
        links: d.links ?? undefined,
        developerName: d.developerName ?? undefined,
      })),
      rejections: history.rejections.map((r) => ({
        ...r,
        rejectedAt: iso(r.rejectedAt),
        rejectedByName: r.rejectedByName ?? undefined,
      })),
      events: history.events.map((e) => ({
        ...e,
        createdAt: iso(e.createdAt),
      })),
    };
  } catch (error) {
    handleApiError(error, 'read dispute history');
  }
}
export async function apiResolveDisputeDAO(
  ..._args: unknown[]
): Promise<never> {
  throw new Error(
    'Formal dispute resolution is pending an approved authority and settlement policy.'
  );
}
